<script setup lang="ts">
import { computed } from 'vue';
import { useI18nLocalize } from '@/i18n/i18nLocalize';
import { getCurrentLocale } from '@/i18n';
import { getTextVariant } from '@/utils/stringUtils';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useClientCopyComposable } from '@/components/modal/clientCopy/useClientCopyComposable';
import { useModalComposable } from '@/views/composable';
import InputText from 'primevue/inputtext';

const props = defineProps<{
  cruiseClone: any;
  indexC: number;
  isCabinEditPricing: any;
  isEditCabin: boolean;
  isPriceValid: boolean;
  lang: string;
  currency: string;
}>();

const { i18nLocale } = useI18nLocalize();
const { t, $t } = i18nLocale(props.lang);
const currentLanguage = getCurrentLocale();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const { formatCurrency } = useClientCopyComposable(props.lang, props.currency);
const { handleOpenErrorMessage } = useModalComposable();

const emits = defineEmits<{
  (e: 'update:isCabinEditPricing', value: any): void;
  (e: 'update:isEditCabin', value: any): void;
  (e: 'update:isPriceValid', value: any): void;
}>();

const isEditCabin = computed({
  get() {
    return props.isEditCabin;
  },
  set(value) {
    emits('update:isEditCabin', value);
  }
});

const isPriceValid = computed({
  get() {
    return props.isPriceValid;
  },
  set(value) {
    emits('update:isPriceValid', value);
  }
});

const isCabinEditPricing = computed({
  get() {
    return props.isCabinEditPricing;
  },
  set(value) {
    emits('update:isCabinEditPricing', value);
  }
});

const headers = [
  $t('book_form.single'),
  $t('book_form.double'),
  $t('book_form.triple'),
  $t('book_form.quad'),
  $t('client_copy.five'),
  $t('client_copy.six'),
  $t('book_form.child'),
  $t('book_form.child'),
  $t('book_form.child'),
  $t('book_form.infant_pdf')
];
const priceTypes = ['singlePrice', 'doublePrice', 'triplePrice', 'quadPrice', 'fiveInRoomPrice', 'sixInRoomPrice', 'childOnePrice', 'childTwoPrice', 'childThreePrice', 'infantPrice'];

const changeCabinEditPricing = (index: number, indexC: number, status: boolean) => {
  isEditCabin.value = status;
  if (!isPriceValid.value) {
    handleOpenErrorMessage(t('client_copy.new_price_bigger'));
    return;
  }
  isCabinEditPricing.value[indexC][index] = status;
};

const formatPricingHead = (cabinData: any) => {
  return cabinData.code ? `${getTextVariant(currentLanguage, cabinData.cabin, cabinData.cabinFr)} - ${cabinData.code}` : cabinData.cabin;
};

const vCheckPrice = {
  mounted(elParam: any) {
    elParam.setAttribute('data-price', elParam.value.replace(/[^\d.-]/g, ''));
    elParam.addEventListener('blur', () => {
      const oldValueParam = elParam.getAttribute('data-price');
      const newValueParam = elParam.value.replace(/[^\d.-]/g, '');
      if (parseFloat(newValueParam) < parseFloat(oldValueParam)) {
        elParam.classList.add('p-invalid');
        isPriceValid.value = false;
      }
    });
  },
  updated(elParam: any) {
    const oldValueParam = elParam.getAttribute('data-price');
    const newValueParam = elParam.value.replace(/[^\d.-]/g, '');
    if (parseFloat(newValueParam) > parseFloat(oldValueParam)) {
      elParam.classList.remove('p-invalid');
      isPriceValid.value = true;
    }
  }
};
</script>

<template>
  <div class="category-pricing">
    <table class="table-child w-full">
      <thead>
        <tr>
          <th class="hidden" v-for="time in 13" :key="time"></th>
        </tr>
      </thead>
      <tbody>
        <tr class="background-grey">
          <td class="text-sm text-sm w-2 white-space-nowrap">{{ $t('client_copy.category_pricing') }}</td>
          <td v-for="(header, index) in headers" :key="index" class="normal-td text-sm">{{ header }}</td>
          <td class="normal-td text-sm w-6rem text-center">{{ $t('client_copy.number_of_cabins') }}</td>
          <td class="normal-td text-sm text-center" v-if="authorizations?.my_quote?.client_copy_edit">{{ $t('common.action') }}</td>
        </tr>
        <template v-for="(cabin, indexCabin) in cruiseClone[indexC].cabins" :key="indexCabin">
          <tr>
            <td class="normal-td-text pl-2 text-sm"></td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm"></td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm"></td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm"></td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm"></td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm"></td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm"></td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm">{{ cabin.occupancies?.childOnePrice || '' }}</td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm">{{ cabin.occupancies?.childTwoPrice || '' }}</td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm">{{ cabin.occupancies?.childThreePrice || '' }}</td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm">{{ cabin.occupancies?.infantPrice || '' }}</td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin] && indexCabin === 0" :rowspan="cruiseClone[indexC].cabins.length * 4" class="normal-td text-sm text-center">{{ cruiseClone[indexC].cabinNo }}</td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" rowspan="4" class="normal-td w-1 text-sm text-center">
              <i v-if="authorizations?.my_quote?.client_copy_edit" class="pi pi-pencil cursor-pointer p-1 normal-td-text" @click="changeCabinEditPricing(indexCabin, indexC, true)"></i>
            </td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text"></td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text"></td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text"></td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text"></td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text"></td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text"></td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text">
              <InputText v-check-price v-model="cabin.occupancies.childOnePrice" type="text" class="w-full text-center" />
            </td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text">
              <InputText v-check-price v-model="cabin.occupancies.childTwoPrice" type="text" class="w-full text-center" />
            </td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text">
              <InputText v-check-price v-model="cabin.occupancies.childThreePrice" type="text" class="w-full text-center" />
            </td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text">
              <InputText v-check-price v-model="cabin.occupancies.infantPrice" type="text" class="w-full text-center" />
            </td>
            <td v-if="indexCabin === 0" v-show="isCabinEditPricing[indexC][indexCabin]" :rowspan="cruiseClone[indexC].cabins.length * 4" class="normal-td">
              {{ cruiseClone[indexC].cabinNo }}
            </td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" rowspan="4" class="text-sm text-center w-1">
              <i class="pi pi-check text-cyan-500 cursor-pointer p-1 normal-td-text" @click="changeCabinEditPricing(indexCabin, indexC, false)"></i>
            </td>
          </tr>
          <tr>
            <td class="normal-td-text pl-2 text-sm">{{ formatPricingHead(cabin) }}</td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm">{{ formatCurrency(cabin.sellingPrice?.singlePrice) }}</td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm">{{ formatCurrency(cabin.sellingPrice?.doublePrice) }}</td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm">{{ formatCurrency(cabin.sellingPrice?.triplePrice) }}</td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm">{{ formatCurrency(cabin.sellingPrice?.quadPrice) }}</td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm">{{ formatCurrency(cabin.sellingPrice?.fiveInRoomPrice) }}</td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm">{{ formatCurrency(cabin.sellingPrice?.sixInRoomPrice) }}</td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm">{{ formatCurrency(cabin.sellingPrice?.childOnePrice) }}</td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm">{{ formatCurrency(cabin.sellingPrice?.childTwoPrice) }}</td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm">{{ formatCurrency(cabin.sellingPrice?.childThreePrice) }}</td>
            <td v-if="!isCabinEditPricing[indexC][indexCabin]" class="normal-td text-sm">{{ formatCurrency(cabin.sellingPrice?.infantPrice) }}</td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text">
              <InputText v-check-price v-model="cabin.sellingPrice.singlePrice" focus type="number" class="w-full text-center" />
            </td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text">
              <InputText v-check-price v-model="cabin.sellingPrice.doublePrice" type="number" class="w-full text-center" />
            </td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text">
              <InputText v-check-price v-model="cabin.sellingPrice.triplePrice" type="number" class="w-full text-center" />
            </td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text">
              <InputText v-check-price v-model="cabin.sellingPrice.quadPrice" type="number" class="w-full text-center" />
            </td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text">
              <InputText v-check-price v-model="cabin.sellingPrice.fiveInRoomPrice" type="number" class="w-full text-center" />
            </td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text">
              <InputText v-check-price v-model="cabin.sellingPrice.sixInRoomPrice" type="number" class="w-full text-center" />
            </td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text">
              <InputText v-check-price v-model="cabin.sellingPrice.childOnePrice" type="number" class="w-full text-center" />
            </td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text">
              <InputText v-check-price v-model="cabin.sellingPrice.childTwoPrice" type="number" class="w-full text-center" />
            </td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text">
              <InputText v-check-price v-model="cabin.sellingPrice.childThreePrice" type="number" class="w-full text-center" />
            </td>
            <td v-show="isCabinEditPricing[indexC][indexCabin]" class="normal-td-text">
              <InputText v-check-price v-model="cabin.sellingPrice.infantPrice" type="number" class="w-full text-center" />
            </td>
          </tr>
          <tr>
            <td class="pl-4 normal-td-text text-sm">{{ $t('client_copy.taxes_and_fees') }}</td>
            <td v-for="priceType in priceTypes" :key="priceType" class="normal-td text-sm">{{ formatCurrency(cabin.taxes?.[priceType]) }}</td>
          </tr>
          <tr>
            <td class="pl-4 normal-td-text text-sm">{{ $t('client_copy.nccf_fare') }}</td>
            <td v-for="priceType in priceTypes" :key="priceType" class="normal-td text-sm">{{ formatCurrency(cabin.fare?.[priceType]) }}</td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>
